import React, { useState, useEffect, useMemo, JSXElementConstructor, Key, ReactElement } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Router, useThemeProvider, usePluginProvider } from '@cloudpremise/portal_core';
import routes from '../routes';
import menus from "../menus";
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "@cloudpremise/portal_core/src/themes/mui/context";
import Sidenav from "@cloudpremise/portal_core/src/themes/mui/examples/Sidenav";
import Configurator from "@cloudpremise/portal_core/src/themes/mui/examples/Configurator";
import themeDark from "@cloudpremise/portal_core/src/themes/mui/assets/theme-dark";
import theme from "@cloudpremise/portal_core/src/themes/mui/assets/theme";

// RTL plugins
import createCache from "@emotion/cache";

import Config from "../Config";
const cdnUrl = Config.getCdnUrl();

// Images
const brandWhite = cdnUrl + "/assets/images/logo-ct.png";
const brandDark = cdnUrl + "/assets/images/logo-ct-dark.png";


function CustomRouter({page, basename, history}){
    const [controller, dispatch] = useMaterialUIController();
    const {
        miniSidenav,
        direction,
        layout,
        openConfigurator,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
        authorized,
        user,
        config
    } = controller;
    const { portalConfig } = config;
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const { pathname } = useLocation();
    const pluginManager = usePluginProvider();
    let bodyClass = "layout_page"+pathname.replace(/\//g,"_");
    bodyClass = bodyClass.replace(/-/g,"_");
    if(darkMode){
      bodyClass += " dark-mode";
    }
    const pathnameCopy = pathname.replace(/^\/+/g, '');
    let isAdminSection = false;
    if(typeof(pathnameCopy) === "string" && pathnameCopy.split("/")[0] === "admin"){
      isAdminSection = true;
      bodyClass += " admin-section";
    }

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  let routeKey = 0;
  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          routeKey++;
          return <Route path={route.route} element={route.component} key={routeKey} />;
        }

        return null;
      }
    );

    const getAppTitle = () => {
      if(typeof(portalConfig) === "object" && portalConfig.hasOwnProperty("portalName")){
        return portalConfig.portalName;
      }
      return Config.getAppTitle();
    };

    const configsButton = (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3.25rem"
          height="3.25rem"
          bgColor={(darkMode ? "dark" : "white")}
          shadow="sm"
          borderRadius="50%"
          position="fixed"
          right="2rem"
          bottom="2rem"
          zIndex={99}
          color={(darkMode ? "white" : "dark")}
          sx={{ cursor: "pointer" }}
          onClick={handleConfiguratorOpen}
          className="portal-config"
        >
          <Icon fontSize="small" color="inherit">
            settings
          </Icon>
        </MDBox>
    );

    const pluginUserMenus = pluginManager.getUserMenus();
    let routesList = routes({page, basename, history});
    let menusList = menus({pluginUserMenus: pluginUserMenus});
    const pluginRoutes = pluginManager.getRoutes({page, basename, history});
    routesList = pluginRoutes.concat(routesList);
    const pluginMenus = pluginManager.getMenus();
    const adminMenus = pluginManager.getAdminMenus();
    if(user !== null && isAdminSection){
      menusList = adminMenus;
    }else{
      menusList = menusList.concat(pluginMenus);
    }
    
    return (
        <div className={bodyClass}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName={getAppTitle()}
                routes={menusList}
                // onMouseEnter={handleOnMouseEnter}
                // onMouseLeave={handleOnMouseLeave}
                className="portal-sidebar"
              />
              {
                user !== null && user.superAdmin ?
                  <>
                    <Configurator />
                    {configsButton}
                  </>
                :
                  null
              }
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            {getRoutes(routesList)}
            <Route path="*" element={<Navigate to={"/servicedesk/cases"+window.location.search} />} />
          </Routes>
        </div>
    );

}

export default CustomRouter;